import { useCallback } from 'react'
import { Trans } from 'react-i18next'

import { actionLabelMap, IActionType } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { useWorkflowNodeContext } from '../WorkflowActionFormModal'
import { PanelsMap } from './panels'
import { PanelProps } from './panels/panel.type'

type ActionInputPanelProps = PanelProps

export default function ActionInputPanel({ panelType, actionType, setValue }: Omit<ActionInputPanelProps, 'data'>) {
  const nodeContext = useWorkflowNodeContext()

  const Panel = PanelsMap[actionType as keyof typeof PanelsMap]
  const nodeType = nodeContext?.previousNode?.data?.actionValue?.type

  const typeName = actionLabelMap[nodeType as keyof typeof actionLabelMap] || nodeType
  const nodeLabel = nodeContext?.previousNode?.data?.actionValue?.label ?? ''
  const currentNode = nodeContext?.previousNode

  const getInputData = useCallback(() => {
    switch (actionType) {
      case IActionType.TemporaryVariable:
        return {
          value: currentNode?.data?.actionValue?.settings?.value,
          allActions: currentNode?.data?.actionValue?.settings?.allActions,
        }
      case IActionType.FilterData:
        return {
          chosenTableId: currentNode?.data?.actionValue?.settings?.tableId,
          conditions: currentNode?.data?.actionValue?.settings?.conditions,
          returnFirstOnly: currentNode?.data?.actionValue?.settings?.returnFirstOnly,
        }
      default:
        return {}
    }
  }, [
    actionType,
    currentNode?.data?.actionValue?.settings?.allActions,
    currentNode?.data?.actionValue?.settings?.tableId,
    currentNode?.data?.actionValue?.settings?.conditions,
    currentNode?.data?.actionValue?.settings?.returnFirstOnly,
    currentNode?.data?.actionValue?.settings?.value,
  ])

  if (!Panel) {
    return null
  }

  return (
    <div className='w-[400px]'>
      <div className='h-full flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'>
        <div className='border-b border-gray-200 dark:border-gray-700 p-4'>
          <div className='flex items-center gap-2'>
            <MaterialIcon icon='input' size={20} className='text-gray-600 dark:text-gray-400' />
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
              <Trans>Input</Trans>
            </h3>
          </div>
          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
            <Trans>Data coming into this step</Trans>
          </p>
        </div>

        <div className='flex-1 p-4 space-y-4'>
          <div className='space-y-4'>
            <div>
              <div className='text-sm font-medium text-gray-900 dark:text-white mb-2'>
                <Trans>Previous Step:</Trans>
              </div>
              <div className='bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600'>
                <div className='text-sm font-medium text-gray-900 dark:text-white'>{nodeLabel}</div>
                <div className='text-xs text-gray-500 dark:text-gray-400 mt-1'>Type: {typeName}</div>
              </div>
            </div>

            {Panel && <Panel data={getInputData()} panelType={panelType} actionType={actionType} setValue={setValue} />}
          </div>
        </div>
      </div>
    </div>
  )
}
